import React, { useRef, useEffect } from "react";
import { useVSU } from "../store";
import { pick, fmtDate, posterDataUrl } from "../helpers";
import EmptyState from "./EmptyState";

// Область воспроизведения (5.5, RQ.20.03/RQ.20.06).
// Нативный <video controls preload="metadata"> 16:9 — воспроизведение средствами браузера.
// containerRef — контейнер .vsu-player, на нём выполняется requestFullscreen (5.6).
export default function VideoPlayer({ containerRef }) {
  const { t, lang, selected, selectedId, setPlaying } = useVSU();
  const videoRef = useRef(null);

  const available = !!selected?.video?.available;
  const url = selected?.video?.url || null;

  // Переключение записи (5.5) — строгая последовательность действий при смене selectedId:
  // pause() → removeAttribute("src") → load() (отмена сетевой загрузки и сброс буфера) →
  // установка нового src и постера, позиция в начало. Так исключается одновременное
  // звучание двух записей и фоновая докачка отменённого файла.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    el.pause();
    el.removeAttribute("src");
    el.load();

    if (available && url) {
      el.setAttribute("src", url);
      el.load();
      try {
        el.currentTime = 0;
      } catch {
        /* до метаданных currentTime может быть недоступен */
      }
    }
    // selectedId в зависимостях — переинициализация даже при совпадающем url у разных записей.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, url, available]);

  // Пустая выборка — «ничего не выбрано». Ловим и selectedId === null,
  // и рассинхрон selected === null при непустом selectedId (реконсиляция после клика по разрезу).
  if (selectedId === null || !selected) {
    return (
      <div className="vsu-player-box">
        <div className="vsu-player" ref={containerRef}>
          <EmptyState icon="▤" title={t("emptyT")} desc={t("emptyD")} />
        </div>
      </div>
    );
  }

  // АС1 UC.12.01 — видеозапись недоступна (video.available === false).
  if (!available) {
    return (
      <div className="vsu-player-box">
        <div className="vsu-player" ref={containerRef}>
          <div className="vsu-noplay">
            <div className="vsu-noplay-ic">⚠</div>
            <b>{t("noVideoT")}</b>
            <div>{t("noVideoD")}</div>
          </div>
        </div>
        <Meta selected={selected} lang={lang} t={t} />
      </div>
    );
  }

  // Доступная запись.
  return (
    <div className="vsu-player-box">
      <div className="vsu-player" ref={containerRef}>
        <video
          ref={videoRef}
          className="vsu-video"
          controls
          preload="metadata"
          poster={posterDataUrl(selected, lang)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        />
      </div>
      <Meta selected={selected} lang={lang} t={t} />
    </div>
  );
}

// Метаданные под плеером. Отсутствующие даты — «—». Имя видеофайла справочно (не идентификатор).
function Meta({ selected, lang, t }) {
  if (!selected) return null; // защита: выборка могла опустеть после клика по разрезу
  const v = selected.video || {};
  const f = v.available ? v.file_name : null;
  return (
    <div className="vsu-pmeta">
      <div>
        {t("colSrc")}: <b>{pick(selected.source_name, lang)}</b>
      </div>
      <div>
        {t("colLoc")}: <b>{pick(selected.location, lang)}</b>
      </div>
      <div>
        {t("colAddr")}: <b>{pick(selected.address, lang)}</b>
      </div>
      <div>
        {t("colFile")}: <b>{f || "—"}</b>
      </div>
      <div>
        {t("colPlan")}: <b>{fmtDate(selected.plan_date) || "—"}</b>
      </div>
      <div>
        {t("colFact")}: <b>{fmtDate(selected.fact_date) || "—"}</b>
      </div>
    </div>
  );
}
